import { Vector3 } from 'three';

import { getFaceNormal, Point2d } from '../../viewer/utilities/math-helpers';
import { roundToDecimals } from './number';

/**
 * Calculates the slope in percent of a vector
 */
export const vectorToSlopePercent = (vector: Vector3) => {
  const length = vector.clone().setZ(0).length();
  return length === 0 ? 0 : vector.z / length / 0.01;
};

export function slopePercentToDegrees(slopePercent: number): number {
  const slopeRatio = slopePercent / 100;
  const angleInRadians = Math.atan(slopeRatio);
  return Math.abs(angleInRadians * (180 / Math.PI));
}

/**
 * Gets the angle (degrees) from the horizontal plane of a slope in percent, negative if it goes down.
 */
export const slopePercentToSignedDegrees = (slopePercent: number) =>
  (Math.atan(slopePercent / 100) * 180) / Math.PI;

/**
 * Gets the slope in percent of an angle (degrees) from the horizontal plane.
 */
export const slopeDegreesToPercent = (degrees: number) => Math.tan((degrees * Math.PI) / 180) * 100;

/**
 * Gets the height difference a given slope and distance.
 * For every meter there should be a 0.01 m difference (1 cm/%)
 */
export const slopePercentToHeightDiff = (percent: number, distance: number) => {
  return distance * percent * 0.01;
};

/**
 * Gets the percent slope of a triangle
 */
export const slopePercentFromTriangle = (f1: Vector3, f2: Vector3, f3: Vector3) => {
  const normal = getFaceNormal(f1, f2, f3);
  // Ground plane normal.
  const groundPlane = new Vector3(0, 0, 1);
  const dot = normal.dot(groundPlane);

  // The angle between the normal of the face and the normal of the ground plane
  // is the angle of the plane.
  const angle = Math.acos(dot);

  const x = 1;
  const z = Math.tan(angle) * x;

  return Math.abs(vectorToSlopePercent(new Vector3(x, 0, z)));
};

/**
 * Gets the fraction of a given slope percent.
 * For example 20 % should result in a 1 : 5 fraction and 300 % should be 3 : 1
 *
 * If the fraction is too granular a decimal value will be returned instead.
 * For example: 6:07 : 1
 */
export const fractionFromSlopePercent = (percent: number): Point2d => {
  if (Math.abs(percent) > Number.MAX_SAFE_INTEGER) {
    return { x: 0, y: 1 };
  }

  const x = 1;
  const y = roundToDecimals(slopePercentToHeightDiff(1, percent), 4);
  const factor = getEvenFactor(y);

  return {
    x: Math.abs(x * factor),
    y: Math.abs(roundToDecimals(y * factor, 4)),
  };
};

/**
 * Gets an even factor that when the value is multiplied by it the result will be an even number.
 *
 * If there are too many iterations it will return 1;
 */
const getEvenFactor = (value: number) => {
  const countDecimals = (x: number) => (x % 1).toString().length - 2;

  let factor = Math.pow(10, countDecimals(value));

  if (factor < 1) {
    return 1;
  }

  let result = value * factor;

  const divisible = (x: number, value: number) => x % value === 0;

  const divisibleByFive = () => divisible(result, 5) && divisible(factor, 5);
  const divisibleByTwo = () => divisible(result, 2) && divisible(factor, 2);

  while (divisibleByFive() || divisibleByTwo()) {
    const value = divisibleByFive() ? 5 : 2;
    factor /= value;
    result /= value;
  }

  return factor > 1000 ? 1 : factor;
};

/**
 * Formats a slope as a ratio, percent and angle, e.g. "1 : 20 (5 % / 2.86°)".
 *
 * @param signed - If true, the slope is shown with + when it goes up and - when it goes down.
 */
export const formatSlope = (percent: number, signed = false) => {
  const fraction = fractionFromSlopePercent(percent);
  const roundedPercent = roundToDecimals(percent, 2);
  const degrees = roundToDecimals(slopePercentToDegrees(percent), 2);
  let [rise, run] = [roundToDecimals(fraction.y, 2), roundToDecimals(fraction.x, 2)];

  // Small slopes without an even ratio, e.g. 0.33 %, would round to 0 : 1, so they are shown as 1 : run.
  if (rise === 0 && roundedPercent !== 0) {
    [rise, run] = [1, roundToDecimals(100 / Math.abs(percent), 2)];
  }

  const ratio = `${rise.toString()} : ${run.toString()}`;

  if (!signed) {
    return `${ratio} (${roundedPercent.toString()} % / ${degrees.toString()}°)`;
  }

  // The ratio and the angle are always positive, so they all get the sign of the slope.
  const sign = roundedPercent > 0 ? '+' : roundedPercent < 0 ? '-' : '';
  return `${sign}${ratio} (${sign}${Math.abs(roundedPercent).toString()} % / ${sign}${Math.abs(degrees).toString()}°)`;
};
